import type { Song } from '@/music/song';
import type { DrawnStaff } from '@/music/notation/drawn';
import { curveObstacles } from '@/music/notation/curveObstacles';

// The slur bezier solve, extracted pure so it can be unit-tested against
// collision cases (the Ave Maria's melisma slur rode exactly on its beam).
// Coordinates are the renderer's pixel space; `s` is the staff-space size.

export interface SlurObstacle {
  x: number;
  left?: number;
  right?: number;
  top: number;
  bot: number;
}

export interface SlurInput {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  above: boolean;
  s: number;
  /** Endpoint anchored at a stem TIP on the slur's side — the beam line for
   *  a beamed group. Such an endpoint needs extra lift or the curve's low
   *  legs run along the beam within its thickness. */
  tipAnchor1?: boolean;
  tipAnchor2?: boolean;
  /** Everything inside the span the curve must clear (head+stem extents). */
  obstacles: SlurObstacle[];
}

export interface SlurCurve {
  ax1: number;
  ay1: number;
  cx1: number;
  cy1: number;
  cx2: number;
  cy2: number;
  ax2: number;
  ay2: number;
}

/** x on the cubic at parameter t. */
export function slurXAt(c: SlurCurve, t: number): number {
  return (
    (1 - t) ** 3 * c.ax1 +
    3 * (1 - t) ** 2 * t * c.cx1 +
    3 * (1 - t) * t ** 2 * c.cx2 +
    t ** 3 * c.ax2
  );
}

/** y on the cubic at parameter t (x(t) is near-linear for these controls). */
export function slurYAt(c: SlurCurve, t: number): number {
  return (
    (1 - t) ** 3 * c.ay1 +
    3 * (1 - t) ** 2 * t * c.cy1 +
    3 * (1 - t) * t ** 2 * c.cy2 +
    t ** 3 * c.ay2
  );
}

/** Invert monotone cubic x: shoulders lie between the two endpoints. */
export function slurTAtX(c: SlurCurve, x: number): number {
  if (x <= c.ax1) return 0;
  if (x >= c.ax2) return 1;
  let lo = 0,
    hi = 1;
  for (let i = 0; i < 32; i++) {
    const t = (lo + hi) / 2;
    if (slurXAt(c, t) < x) lo = t;
    else hi = t;
  }
  return (lo + hi) / 2;
}

/**
 * The engraved slur shape: a cubic with two shoulders at 25%/75%, so the
 * curve rises quickly, runs a rounded top, and settles — a quadratic's legs
 * dive near the endpoints and clip notes there. POINTWISE clearance: for
 * every obstacle column inside the span, the bezier's y at that x must clear
 * the obstacle's extents; violations push both shoulders out, weighted by
 * the control basis at t.
 */
export interface SlurTextBox {
  x0: number;
  x1: number;
  /** center y of the text line */
  yc: number;
  halfH: number;
}

/**
 * Where a text struck by a slur should move — "move the text, not the arc".
 * Texts are deliberately NOT solveSlur obstacles: bending can't clear a text
 * near an anchored endpoint (the clearance weight vanishes there, so the
 * limb strikes straight through), and mid-span it spikes the arc into a
 * grotesque hump. Instead the text steps past the curve on its own side —
 * an above-staff cue climbs over an above slur, a below cue sinks under a
 * below slur. Returns the new center y, or null when the text is clear.
 */
export function slurTextDodge(
  c: SlurCurve,
  box: SlurTextBox,
  above: boolean,
  s: number,
): number | null {
  const left = Math.max(c.ax1, box.x0 - 0.2 * s);
  const right = Math.min(c.ax2, box.x1 + 0.2 * s);
  if (right < left) return null;
  const { top: minY, bottom: maxY } = slurVerticalBounds(c, slurTAtX(c, left), slurTAtX(c, right));
  const top = box.yc - box.halfH;
  const bot = box.yc + box.halfH;
  if (maxY < top - 0.2 * s || minY > bot + 0.2 * s) return null;
  return above ? minY - box.halfH - 0.4 * s : maxY + box.halfH + 0.4 * s;
}

export function solveSlur(input: SlurInput): SlurCurve {
  const { x1, y1, x2, y2, above, s, obstacles } = input;
  const lift = above ? -1 : 1;
  const ay1 = y1 + lift * (input.tipAnchor1 ? 1.15 : 0.6) * s;
  const ay2 = y2 + lift * (input.tipAnchor2 ? 1.15 : 0.6) * s;
  const dx = x2 - x1;
  const rise = Math.min(1.8, 0.6 + Math.abs(dx) / (18 * s)) * s;
  const cx1 = x1 + 0.25 * dx;
  const cx2 = x1 + 0.75 * dx;
  const curve: SlurCurve = {
    ax1: x1,
    ay1,
    cx1,
    cy1: ay1 + (ay2 - ay1) * 0.25 + lift * rise,
    cx2,
    cy2: ay1 + (ay2 - ay1) * 0.75 + lift * rise,
    ax2: x2,
    ay2,
  };
  // Endpoints must clear ink that reaches their x, before solving shoulders.
  for (const e of obstacles) {
    const left = e.left ?? e.x,
      right = e.right ?? e.x;
    const bound = above ? e.top - 0.35 * s : e.bot + 0.35 * s;
    for (const end of [1, 2] as const) {
      const x = end === 1 ? x1 : x2;
      if (x < left || x > right) continue;
      const key = end === 1 ? 'ay1' : 'ay2';
      const shift = above ? Math.min(0, bound - curve[key]) : Math.max(0, bound - curve[key]);
      curve[key] += shift;
      curve[end === 1 ? 'cy1' : 'cy2'] += shift;
    }
  }
  // A tall mark just beside an endpoint should lift that end, rather than
  // forcing both shoulders into a needle-like arch over a short phrase.
  for (const e of obstacles) {
    const left = Math.max(x1, e.left ?? e.x),
      right = Math.min(x2, e.right ?? e.x);
    if (right < left) continue;
    const lo = slurTAtX(curve, left),
      hi = slurTAtX(curve, right);
    for (let sample = 0; sample <= 8; sample++) {
      const t = lo + ((hi - lo) * sample) / 8;
      if (t < 1e-6 || t > 1 - 1e-6 || (t >= 0.25 && t <= 0.75)) continue;
      const gap = (0.35 + 0.5 * Math.min(1, t / 0.1, (1 - t) / 0.1)) * s;
      const bound = above ? e.top - gap : e.bot + gap;
      const violation = above ? slurYAt(curve, t) - bound : bound - slurYAt(curve, t);
      if (violation / (3 * t * (1 - t)) <= 2 * s) continue;
      const atStart = t < 0.5;
      const basis = atStart ? (1 - t) ** 2 * (1 + 2 * t) : t ** 2 * (3 - 2 * t);
      const shift = (lift * violation) / basis;
      curve[atStart ? 'ay1' : 'ay2'] += shift;
      curve[atStart ? 'cy1' : 'cy2'] += shift;
    }
  }
  for (let pass = 0; pass < 3; pass++) {
    let worst = 0;
    for (const e of obstacles) {
      const left = Math.max(x1, e.left ?? e.x),
        right = Math.min(x2, e.right ?? e.x);
      if (right < left) continue;
      const lo = slurTAtX(curve, left),
        hi = slurTAtX(curve, right);
      for (let sample = 0; sample <= 8; sample++) {
        const t = lo + ((hi - lo) * sample) / 8;
        if (t < 1e-6 || t > 1 - 1e-6) continue;
        const gap = (0.35 + 0.5 * Math.min(1, t / 0.1, (1 - t) / 0.1)) * s;
        const bound = above ? e.top - gap : e.bot + gap;
        const violation = above ? slurYAt(curve, t) - bound : bound - slurYAt(curve, t);
        // Use the actual shoulder basis; a clamped weight left collisions
        // beside the endpoints even after the previous repeated solve.
        worst = Math.max(worst, violation / (3 * t * (1 - t)));
      }
    }
    if (worst <= 0.01) break;
    curve.cy1 += lift * worst;
    curve.cy2 += lift * worst;
  }
  return curve;
}

export function slurVerticalBounds(c: SlurCurve, t0 = 0, t1 = 1): { top: number; bottom: number } {
  const a = -c.ay1 + 3 * c.cy1 - 3 * c.cy2 + c.ay2;
  const b = 3 * c.ay1 - 6 * c.cy1 + 3 * c.cy2;
  const d = -3 * c.ay1 + 3 * c.cy1;
  const ts = [t0, t1];
  if (Math.abs(a) < 1e-12) {
    if (Math.abs(b) > 1e-12) ts.push(-d / (2 * b));
  } else {
    const disc = 4 * b * b - 12 * a * d;
    if (disc >= 0)
      ts.push((-2 * b + Math.sqrt(disc)) / (6 * a), (-2 * b - Math.sqrt(disc)) / (6 * a));
  }
  const ys = ts.filter((t) => t >= t0 && t <= t1).map((t) => slurYAt(c, t));
  return { top: Math.min(...ys), bottom: Math.max(...ys) };
}

export interface StaffSlurCurve {
  si: number;
  key: string;
  fromId: string;
  toId: string;
  c: SlurCurve;
}

/** Build every segment in staff spaces, before row and page placement. */
export function staffSlurs(drawn: DrawnStaff, slurs: Song['slurs']): StaffSlurCurve[] {
  if (!slurs?.length) return [];
  const boxes = drawn.systems.map((sys) => curveObstacles(sys.measures));
  const anchors = new Map<
    string,
    { si: number; x: number; top: number; bottom: number; stemUp: boolean; order: number }
  >();
  let order = 0;
  drawn.systems.forEach((sys, si) => {
    for (const m of sys.measures)
      for (const n of m.notes) {
        const ink = boxes[si].filter((b) => b.sourceId === n.sourceId);
        anchors.set(n.sourceId, {
          si,
          x: m.x + n.x,
          top: Math.min(n.y, ...ink.map((b) => b.top)),
          bottom: Math.max(n.y, ...ink.map((b) => b.bottom)),
          stemUp: n.stem ? n.stem.y2 < n.stem.y1 : n.y >= 2,
          order: order++,
        });
      }
  });
  const out: StaffSlurCurve[] = [];
  const placed: { si: number; above: boolean; c: SlurCurve }[] = [];
  // Inner phrases claim the closest lane first, independent of import order.
  const sorted = slurs
    .map((sl, index) => ({ sl, index }))
    .sort((a, b) => {
      const span = (sl: NonNullable<Song['slurs']>[number]) =>
        (anchors.get(sl.toId)?.order ?? 0) - (anchors.get(sl.fromId)?.order ?? 0);
      return span(a.sl) - span(b.sl) || a.index - b.index;
    });
  sorted.forEach(({ sl, index }) => {
    const a = anchors.get(sl.fromId),
      b = anchors.get(sl.toId);
    if (!a || !b || b.si < a.si) return;
    const above = sl.side ? sl.side === 'above' : !a.stemUp;
    for (let si = a.si; si <= b.si; si++) {
      const first = si === a.si,
        last = si === b.si;
      const x1 = first ? a.x : 0.3,
        x2 = last ? b.x : drawn.systems[si].width - 0.1;
      if (x2 <= x1) continue;
      const edgeY = (x: number) => {
        const near = boxes[si].filter((box) => box.left <= x + 2 && box.right >= x - 2);
        return above
          ? Math.min(0, ...near.map((box) => box.top))
          : Math.max(4, ...near.map((box) => box.bottom));
      };
      const obstacles: SlurObstacle[] = boxes[si].map((box) => ({
        x: (box.left + box.right) / 2,
        left: box.left,
        right: box.right,
        top: box.top,
        bot: box.bottom,
      }));
      for (const inner of placed) {
        if (inner.si !== si || inner.above !== above) continue;
        const lo = Math.max(x1, inner.c.ax1),
          hi = Math.min(x2, inner.c.ax2);
        if (hi - lo < 0.1) continue;
        // Narrow, exact envelopes keep the outer curve rounded rather than
        // clearing one tall bounding rectangle over the inner phrase.
        const steps = Math.ceil((hi - lo) / 0.4);
        for (let k = 0; k < steps; k++) {
          const left = lo + ((hi - lo) * k) / steps;
          const right = lo + ((hi - lo) * (k + 1)) / steps;
          const bounds = slurVerticalBounds(
            inner.c,
            slurTAtX(inner.c, left),
            slurTAtX(inner.c, right),
          );
          obstacles.push({
            x: (left + right) / 2,
            left,
            right,
            top: bounds.top - 0.07,
            bot: bounds.bottom + 0.07,
          });
        }
      }
      const c = solveSlur({
        x1,
        x2,
        y1: first ? (above ? a.top : a.bottom) : edgeY(x1),
        y2: last ? (above ? b.top : b.bottom) : edgeY(x2),
        above,
        s: 1,
        tipAnchor1: first && above === a.stemUp,
        tipAnchor2: last && above === b.stemUp,
        obstacles,
      });
      out.push({ si, key: `slur-${index}-${si}`, fromId: sl.fromId, toId: sl.toId, c });
      placed.push({ si, above, c });
    }
  });
  return out;
}
